/**
 * CuttySark 賞（四半期で最も成果を出した人の表彰）の予選・本戦を進行する API 契約 — フロント/バック/MCP 共有の単一 SoT。
 *
 * 1 回の予選（チーム予選・グループ予選・本戦のいずれか 1 回）を「セッション」と呼び、
 * Firestore `cuttysark_sessions/{sessionId}` の 1 ドキュメントに部門・候補者・参加者・進行状態を持つ。
 * 投票は配下の `ballots/{voterEmail}` に 1 人 1 件で保存し、出し直しは上書きする。
 *
 * 採点（業務成果・未来投資 各 0〜10 点）は各自が投票先を決めるための材料で、順位は決めない。
 * 順位を決めるのは部門ごとの投票（票数は部門ごと。PDG はメンバー部門 1 人 2 票・リーダー部門 1 人 1 票。
 * 自分には入れない・同じ人に複数票は入れない）で、
 * 締切時に集計した結果をセッションに焼き込む。参加者には合計票と順位だけを見せ、
 * 誰が誰に入れたかは運営しか見られない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞の予選・本戦セッション API 契約 (Zod schema + 推論型) の単一 SoT
 * @graph-connects none
 */
import { z } from 'zod';
/** 予選の段階。チーム予選 → グループ予選 → 本戦。@graph-connects none */
export const CUTTYSARK_STAGES = ['team', 'group', 'final'];
/**
 * セッションの進行状態。
 * - preparing: 準備中（候補者・参加者を登録する）
 * - intro: 代表者紹介（ステージ画面で 1 人ずつ登場させる）
 * - presenting: 発表中（currentCandidateEmail の人が発表している）
 * - voting: 採点・投票の受付中
 * - closed: 締切済み（集計済み・未発表）
 * - revealing: 結果発表中（部門ごとに下位から順にめくる）
 * - finished: 終了（全結果を公開）
 * @graph-connects none
 */
export const CUTTYSARK_PHASES = [
    'preparing',
    'intro',
    'presenting',
    'voting',
    'closed',
    'revealing',
    'finished',
];
/** 投票者の決め方。listed は登録した人だけ、anyone はログインしている全員。@graph-connects none */
export const CUTTYSARK_VOTER_MODES = ['listed', 'anyone'];
/** 採点の下限・上限（業務成果・未来投資それぞれ）。@graph-connects none */
export const CUTTYSARK_SCORE_MIN = 0;
/** @graph-connects none */
export const CUTTYSARK_SCORE_MAX = 10;
/** 応援コメントの最大文字数。@graph-connects none */
export const CUTTYSARK_COMMENT_MAX_LENGTH = 1000;
/** 二つ名の最大文字数。@graph-connects none */
export const CUTTYSARK_TITLE_MAX_LENGTH = 60;
/** 紹介文の最大文字数。@graph-connects none */
export const CUTTYSARK_INTRO_MAX_LENGTH = 400;
/** 1 部門あたりの 1 人の票数の上限。@graph-connects none */
export const CUTTYSARK_MAX_VOTES_PER_VOTER = 5;
/** 1 セッションの候補者数の上限。@graph-connects none */
export const CUTTYSARK_MAX_CANDIDATES = 40;
/**
 * 結果発表で順位ごとに 1 つずつ（同順位はまとめて）発表する順位の範囲。3 位 → 2 位 → 1 位の順に
 * 発表し、これより下の順位は発表せず、最初の発表と一緒に開く（全員を順に読み上げると間延びするため）。
 * @graph-connects none
 */
export const CUTTYSARK_ANNOUNCED_RANKS = 3;
/** 結果発表を 1 つ進める（next）か 1 つ戻す（back）か。@graph-connects none */
export const CUTTYSARK_REVEAL_DIRECTIONS = ['next', 'back'];
/** 1 セッションの投票者・運営の登録数の上限。@graph-connects none */
export const CUTTYSARK_MAX_MEMBERS = 500;
/** @graph-connects none */
const emailSchema = z.string().trim().toLowerCase().pipe(z.email().max(254));
/** 画像・資料リンク。空文字は「未設定」。@graph-connects none */
const optionalUrlSchema = z
    .string()
    .trim()
    .pipe(z.union([z.literal(''), z.url().max(2000)]));
/** Google Meet の URL。参加者向けにリンク描画するので https のみ許可する。空文字は「未設定」。@graph-connects none */
const optionalHttpsUrlSchema = z
    .string()
    .trim()
    .pipe(z.union([z.literal(''), z.url({ protocol: /^https$/ }).max(2000)]));
/** 部門 ID。URL や Firestore のキーに使うので英小文字・数字・ハイフンに限る。@graph-connects none */
export const cuttysarkDivisionIdSchema = z
    .string()
    .trim()
    .regex(/^[a-z0-9-]{1,40}$/, '部門 ID は英小文字・数字・ハイフンで 40 文字以内にしてください');
/**
 * 部門（メンバー部門 / リーダー部門など）。票数は部門ごとに決める（PDG ではメンバー部門 2 票・リーダー部門 1 票）。
 * @graph-connects none
 */
export const cuttysarkDivisionSchema = z.object({
    id: cuttysarkDivisionIdSchema,
    name: z.string().trim().min(1).max(40),
    /** 1 人がこの部門で投じる票数（既定 2）。 */
    votesPerVoter: z.number().int().min(1).max(CUTTYSARK_MAX_VOTES_PER_VOTER),
    /** この部門から次の段階へ進む人数。 */
    advanceCount: z.number().int().min(1).max(CUTTYSARK_MAX_CANDIDATES),
});
/**
 * 候補者の登録入力。表示名とプロフィール写真はサーバーが users から引いて補う。
 * @graph-connects none
 */
export const cuttysarkCandidateInputSchema = z.object({
    email: emailSchema,
    divisionId: cuttysarkDivisionIdSchema,
    /** 二つ名（例: 「無敵の太郎」）。紹介アニメーションで名前の上に出す。 */
    title: z.string().trim().max(CUTTYSARK_TITLE_MAX_LENGTH).default(''),
    /** 紹介文。 */
    intro: z.string().trim().max(CUTTYSARK_INTRO_MAX_LENGTH).default(''),
    /** 発表資料のリンク。 */
    materialUrl: optionalUrlSchema.default(''),
    /** 紹介用の写真。未設定ならプロフィール写真を使う。 */
    photoUrl: optionalUrlSchema.default(''),
});
/** 保存済みの候補者。@graph-connects none */
export const cuttysarkCandidateSchema = cuttysarkCandidateInputSchema.extend({
    /** users.nickName（無ければメールのローカル部）。 */
    name: z.string(),
    /** Google Workspace のプロフィール写真。取れなければ空文字。 */
    avatarUrl: z.string(),
    /**
     * アップロードした写真の版（0 = 未アップロード）。アップロード時刻（ミリ秒）で、配信 URL に付けて
     * ブラウザのキャッシュを切り替える。削除で 0 に戻っても次の版は過去の版と重ならない。既存の保存データには無いので 0 で補う。
     */
    photoVersion: z.number().int().min(0).default(0),
});
/** アップロードできる写真の形式。@graph-connects none */
export const CUTTYSARK_PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/**
 * アップロードできる写真の最大サイズ（バイト）。画面は長辺 600px の JPEG に縮めてから送るので
 * 通常は 100KB 前後。Firestore の 1 ドキュメント 1MiB 上限に base64 でも十分収まる値にする。
 * @graph-connects none
 */
export const CUTTYSARK_PHOTO_MAX_BYTES = 400 * 1024;
/** 写真のアップロード（base64）。@graph-connects none */
export const putCuttysarkCandidatePhotoRequestSchema = z.object({
    contentType: z.enum(CUTTYSARK_PHOTO_CONTENT_TYPES),
    /** 画像本体の base64（data: プレフィックスは付けない）。 */
    data: z
        .string()
        .min(1)
        .max(Math.ceil((CUTTYSARK_PHOTO_MAX_BYTES * 4) / 3) + 4, '写真は 400KB 以内にしてください')
        .regex(/^[A-Za-z0-9+/]+={0,2}$/, '写真のデータが base64 ではありません'),
});
/**
 * 閲覧者に返す候補者。アップロードした写真があれば、その配信パス（API のベース URL からの相対）を付ける。
 * @graph-connects none
 */
export const cuttysarkLiveCandidateSchema = cuttysarkCandidateSchema.extend({
    /** 例: /cuttysark/sessions/{id}/candidates/{email}/photo?v=2。無ければ空文字。 */
    uploadedPhotoPath: z.string(),
});
/** 部門ごとの開示済み件数（下位から何人めくったか）。@graph-connects none */
export const cuttysarkRevealSchema = z.object({
    divisionId: cuttysarkDivisionIdSchema,
    count: z.number().int().min(0).max(CUTTYSARK_MAX_CANDIDATES),
});
/**
 * 結果発表を順位単位で 1 つ進める・戻す。何人めくるかは API が集計結果から決める
 * （同順位をまとめる・4 位以下は最初の発表で一緒に開く、を入口ごとに書かないため）。
 * @graph-connects none
 */
export const cuttysarkRevealStepSchema = z.object({
    divisionId: cuttysarkDivisionIdSchema,
    direction: z.enum(CUTTYSARK_REVEAL_DIRECTIONS),
});
/** 集計結果の 1 行。@graph-connects none */
export const cuttysarkResultEntrySchema = z.object({
    candidateEmail: z.string(),
    votes: z.number().int().min(0),
    /** 同票は同順位（1 始まり）。 */
    rank: z.number().int().min(1),
    /** 次の段階へ進む人数の枠に入っているか。枠の境目で同票なら運営が決める。 */
    advancing: z.boolean(),
    /** 枠の境目で同票になっている（運営の判断が要る）。 */
    tiedAtBoundary: z.boolean(),
    /** 応援コメント（投票者は伏せる）。 */
    comments: z.array(z.string()),
});
/** 部門ごとの集計結果（順位の高い順）。@graph-connects none */
export const cuttysarkDivisionResultSchema = z.object({
    divisionId: z.string(),
    entries: z.array(cuttysarkResultEntrySchema),
});
/** Firestore に保存するセッション本体。@graph-connects none */
export const cuttysarkSessionDocumentSchema = z.object({
    title: z.string(),
    /** 予選を行う Google Meet の URL（参加者画面に「Meet に参加」として出す）。既存データは空で補う。 */
    meetUrl: z.string().default(''),
    /** 対象の四半期（例: 2026-4Q）。 */
    quarter: z.string(),
    stage: z.enum(CUTTYSARK_STAGES),
    divisions: z.array(cuttysarkDivisionSchema),
    candidates: z.array(cuttysarkCandidateSchema),
    voterMode: z.enum(CUTTYSARK_VOTER_MODES),
    voters: z.array(z.string()),
    operators: z.array(z.string()),
    phase: z.enum(CUTTYSARK_PHASES),
    currentCandidateEmail: z.string().nullable(),
    reveal: z.array(cuttysarkRevealSchema),
    /** 締切時に焼き込んだ集計。投票を再開すると null に戻す。 */
    results: z.array(cuttysarkDivisionResultSchema).nullable(),
    revision: z.number().int().min(0),
    createdBy: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** 1 候補者への採点とコメント。@graph-connects none */
export const cuttysarkScoreSchema = z.object({
    candidateEmail: emailSchema,
    /** 業務成果・業績。 */
    achievement: z.number().int().min(CUTTYSARK_SCORE_MIN).max(CUTTYSARK_SCORE_MAX).nullable(),
    /** 未来への種まき（未来投資）。 */
    future: z.number().int().min(CUTTYSARK_SCORE_MIN).max(CUTTYSARK_SCORE_MAX).nullable(),
    comment: z.string().trim().max(CUTTYSARK_COMMENT_MAX_LENGTH).default(''),
});
/** 部門ごとの投票先。@graph-connects none */
export const cuttysarkDivisionVoteSchema = z.object({
    divisionId: cuttysarkDivisionIdSchema,
    candidateEmails: z.array(emailSchema).max(CUTTYSARK_MAX_VOTES_PER_VOTER),
});
/** 投票の送信内容（出し直しは全置換）。@graph-connects none */
export const putCuttysarkBallotRequestSchema = z.object({
    scores: z.array(cuttysarkScoreSchema).max(CUTTYSARK_MAX_CANDIDATES),
    votes: z.array(cuttysarkDivisionVoteSchema).max(20),
});
/** 保存済みの投票。@graph-connects none */
export const cuttysarkBallotSchema = z.object({
    voterEmail: z.string(),
    scores: z.array(cuttysarkScoreSchema),
    votes: z.array(cuttysarkDivisionVoteSchema),
    submittedAt: z.string(),
});
/** セッション作成の入力。@graph-connects none */
export const createCuttysarkSessionRequestSchema = z.object({
    title: z.string().trim().min(1).max(80),
    quarter: z
        .string()
        .trim()
        .regex(/^\d{4}-[1-4]Q$/, '四半期は 2026-4Q の形式で指定してください'),
    stage: z.enum(CUTTYSARK_STAGES),
});
/**
 * セッションの設定更新（部門・候補者・参加者・運営）。指定した項目だけ全置換する。
 * baseRevision が現在値と違えば 409（複数の運営が同時に触っても黙って踏み潰さない）。
 * @graph-connects none
 */
export const updateCuttysarkSessionRequestSchema = z.object({
    baseRevision: z.number().int().min(0),
    title: z.string().trim().min(1).max(80).optional(),
    /** Google Meet の URL。空文字で未設定に戻す。 */
    meetUrl: optionalHttpsUrlSchema.optional(),
    divisions: z.array(cuttysarkDivisionSchema).min(1).max(10).optional(),
    candidates: z.array(cuttysarkCandidateInputSchema).max(CUTTYSARK_MAX_CANDIDATES).optional(),
    voterMode: z.enum(CUTTYSARK_VOTER_MODES).optional(),
    voters: z.array(emailSchema).max(CUTTYSARK_MAX_MEMBERS).optional(),
    operators: z.array(emailSchema).min(1).max(50).optional(),
});
/**
 * 進行操作。phase を変えるか、発表者の切り替え・結果のめくりを行う（指定した項目だけ反映）。
 * @graph-connects none
 */
export const controlCuttysarkSessionRequestSchema = z.object({
    phase: z.enum(CUTTYSARK_PHASES).optional(),
    currentCandidateEmail: emailSchema.nullable().optional(),
    reveal: cuttysarkRevealSchema.optional(),
    /** 結果発表を順位単位で進める・戻す（運営画面はこちらを使う）。 */
    revealStep: cuttysarkRevealStepSchema.optional(),
});
/** 一覧の 1 行。@graph-connects none */
export const cuttysarkSessionSummarySchema = z.object({
    id: z.string(),
    title: z.string(),
    quarter: z.string(),
    stage: z.enum(CUTTYSARK_STAGES),
    phase: z.enum(CUTTYSARK_PHASES),
    candidateCount: z.number().int(),
    createdAt: z.string(),
});
/** @graph-connects none */
export const cuttysarkSessionListResponseSchema = z.object({
    sessions: z.array(cuttysarkSessionSummarySchema),
    /** セッションを作れるか（作成ボタンの出し分け）。 */
    canCreate: z.boolean(),
});
/**
 * 閲覧者ごとに絞ったセッションの見え方。ステージ画面・参加者画面・運営画面が 2 秒ごとに読む。
 * 運営以外には投票者一覧・未投票者・めくっていない結果を返さない。
 * @graph-connects none
 */
export const cuttysarkLiveViewSchema = z.object({
    id: z.string(),
    title: z.string(),
    /** Google Meet の URL（未設定は空文字）。 */
    meetUrl: z.string(),
    quarter: z.string(),
    stage: z.enum(CUTTYSARK_STAGES),
    divisions: z.array(cuttysarkDivisionSchema),
    candidates: z.array(cuttysarkLiveCandidateSchema),
    phase: z.enum(CUTTYSARK_PHASES),
    currentCandidateEmail: z.string().nullable(),
    reveal: z.array(cuttysarkRevealSchema),
    /** 閲覧者に見せてよい範囲の結果（部門ごと・順位の高い順。未開示の行は含めない）。 */
    results: z.array(cuttysarkDivisionResultSchema),
    revision: z.number().int(),
    viewer: z.object({
        email: z.string(),
        isOperator: z.boolean(),
        canVote: z.boolean(),
        hasSubmitted: z.boolean(),
    }),
    /** 運営だけに返す。 */
    operatorView: z
        .object({
        voterMode: z.enum(CUTTYSARK_VOTER_MODES),
        voters: z.array(z.string()),
        operators: z.array(z.string()),
        submittedVoters: z.array(z.string()),
        /** listed のとき、まだ投票していない人。 */
        pendingVoters: z.array(z.string()),
    })
        .nullable(),
});
/** 自分の投票の取得結果。未投票なら null。@graph-connects none */
export const cuttysarkMyBallotResponseSchema = z.object({
    ballot: cuttysarkBallotSchema.nullable(),
});
/** 運営向けの投票明細。@graph-connects none */
export const cuttysarkBallotsResponseSchema = z.object({
    ballots: z.array(cuttysarkBallotSchema),
});
/** 本人あての応援コメント（終了後のみ・投票者は伏せる）。@graph-connects none */
export const cuttysarkMyCommentsResponseSchema = z.object({
    /** 終了していない、または候補者でなければ false（comments は空）。 */
    available: z.boolean(),
    divisionName: z.string(),
    comments: z.array(z.string()),
});
//# sourceMappingURL=cuttysark.js.map